import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import API from "../../services/API";
import baseElementTemplate from "../../data/baseElement.json";
import { AppButton } from "../ui";
import BulkUploadElementDialog from "./BulkUploadElementDialog";
import { useContent } from "../../context/ContentContext";
import {
  getElementFileConfig,
  getElementUploadContentType,
  isElementImageFile,
} from "../../utility/elementFiles";

const BulkElementUploader = ({ onNewElementCreated }) => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const { upsertMyElement, patchMyElement, trackElementBuild } = useContent();

  const handleOpenDialog = () => {
    setUploadDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setUploadDialogOpen(false);
  };

  const cloneBaseElementTemplate = () => JSON.parse(JSON.stringify(baseElementTemplate));

  const getImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = (err) => reject(err);
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  };

  const createElementPayload = async ({ file, namePrefix, optimization }) => {
    const newElementPayload = cloneBaseElementTemplate();

    const elementId = uuidv4();
    newElementPayload.id = newElementPayload.dataObjectID = elementId;
    newElementPayload.labelData.displayName = `${namePrefix}${file.name}`;
    newElementPayload.labelData.description = file.name;
    newElementPayload.fileName = file.name;
    if (optimization) {
      newElementPayload.optimization = optimization;
    }

    const fileConfig = getElementFileConfig(file.name);
    if (fileConfig?.visualizerData) {
      Object.assign(newElementPayload.visualizerData, fileConfig.visualizerData);
    }
    if (fileConfig?.worldUnit) {
      Object.assign(newElementPayload.worldUnit, fileConfig.worldUnit);
    }

    if (isElementImageFile(file.name)) {
      try {
        const { width, height } = await getImageDimensions(file);
        if (width > 0) {
          newElementPayload.visualizerData.xValue = height / width;
        }
      } catch (err) {
        console.error("Failed to get image dimensions:", err);
      }
    }

    return newElementPayload;
  };

  const uploadSingleElement = async ({ file, namePrefix, optimization }) => {
    const newElementPayload = await createElementPayload({
      file,
      namePrefix,
      optimization,
    });
    const createdElement = {
      id: newElementPayload.id,
      name: newElementPayload.labelData.displayName,
      description: newElementPayload.labelData.description,
      dateModified: new Date().toISOString(),
      status: {
        type: "BUILDING",
        timestamp: new Date().toISOString(),
      },
    };

    try {
      const response = await API.post("elements/", newElementPayload, {
        requiresAuth: true,
      });

      const { sourceUploadUrl } = response.data.data;
      upsertMyElement(createdElement, { prepend: true });

      if (file && sourceUploadUrl) {
        const contentType = getElementUploadContentType(file);
        const uploadHeaders = contentType ? { "Content-Type": contentType } : undefined;
        await fetch(sourceUploadUrl, {
          method: "PUT",
          body: file,
          headers: uploadHeaders,
        });
      }

      await API.post("build-element", newElementPayload, {
        requiresAuth: true,
      });
      trackElementBuild(createdElement.id);

      return createdElement;
    } catch (error) {
      patchMyElement(createdElement.id, {
        status: {
          type: "BUILDING_FAILED",
          timestamp: new Date().toISOString(),
          message: error?.message || "Upload failed",
        },
      });
      throw error;
    }
  };

  const handleBulkUpload = async ({
    namePrefix = "",
    files = [],
    optimization,
  }) => {
    if (!Array.isArray(files) || files.length === 0) {
      return;
    }

    try {
      for (const file of files) {
        const createdElement = await uploadSingleElement({
          file,
          namePrefix,
          optimization,
        });
        if (onNewElementCreated) {
          onNewElementCreated(createdElement);
        }
      }
      handleCloseDialog();
    } catch (error) {
      console.error("Error uploading bulk elements:", error);
      throw error;
    }
  };

  return (
    <>
      <AppButton kind="secondary" onClick={handleOpenDialog}>
        Bulk Upload Elements
      </AppButton>
      <BulkUploadElementDialog
        open={uploadDialogOpen}
        onClose={handleCloseDialog}
        onUpload={handleBulkUpload}
      />
    </>
  );
};

export default BulkElementUploader;
